import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { X, Send, CheckCircle } from 'lucide-react'
import { validateEmail } from '@/utils/helpers'
import { emailService } from '@/services/emailService'

interface PartnershipModalProps {
  isOpen?: boolean
  onClose?: () => void
  type?: 'partnership' | 'referral' // Different types for different buttons
}

const PartnershipModal: React.FC<PartnershipModalProps> = ({ 
  isOpen = false, 
  onClose,
  type = 'partnership'
}) => {
  const { t } = useTranslation()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: ''
  })

  const isFormValid = (() => {
    const { name, email, organization } = formData
    const trimmedName = name?.trim()
    const trimmedEmail = email?.trim()
    const trimmedOrg = organization?.trim()
    
    return !!(
      trimmedName && 
      trimmedEmail && 
      validateEmail(trimmedEmail) &&
      trimmedOrg
    )
  })()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validate form
    const newErrors: Record<string, string> = {}
    
    if (!formData.name.trim()) {
      newErrors.name = t('validation.required')
    }
    
    if (!formData.email.trim()) {
      newErrors.email = t('validation.required')
    } else if (!validateEmail(formData.email)) {
      newErrors.email = t('validation.email')
    }
    
    if (!formData.organization.trim()) {
      newErrors.organization = t('validation.required')
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setIsSubmitting(true)
    
    try {
      // Send to email service
      const result = await emailService.sendPartnershipForm({
        name: formData.name,
        email: formData.email,
        organization: formData.organization,
        partnershipType: type,
        message: `Partnership inquiry for ${type}`
      }, `partnership_${type}_form`)
      
      if (result.success) {
        setIsSubmitted(true)
        setTimeout(() => {
          setIsSubmitted(false)
          setFormData({ name: '', email: '', organization: '' })
          setErrors({})
          if (onClose) onClose()
        }, 3000)
      } else {
        console.error('Failed to send partnership form:', result.message)
        // Still show success but log the error
        setIsSubmitted(true)
        setTimeout(() => {
          setIsSubmitted(false)
          setFormData({ name: '', email: '', organization: '' })
          setErrors({})
          if (onClose) onClose()
        }, 3000)
      }
    } catch (error) {
      console.error('Failed to submit partnership request:', error)
      // Fallback: still show success
      setIsSubmitted(true)
      setTimeout(() => {
        setIsSubmitted(false)
        setFormData({ name: '', email: '', organization: '' })
        setErrors({})
        if (onClose) onClose()
      }, 3000)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({ name: '', email: '', organization: '' })
      setErrors({})
      if (onClose) onClose()
    }
  }

  const getTitle = () => {
    return type === 'partnership' 
      ? 'Become a Partner' 
      : 'Join the Program'
  }

  const getSuccessMessage = () => {
    return type === 'partnership'
      ? 'Thank you for your interest in becoming a partner! We\'ll be in touch soon.'
      : 'Thank you for your interest in our referral program! We\'ll be in touch soon.'
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                {getTitle()}
              </h3>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                disabled={isSubmitting}
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {isSubmitted ? (
              /* Success State */
              <div className="text-center py-8">
                <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Request Submitted!
                </h3>
                <p className="text-gray-600">
                  {getSuccessMessage()}
                </p>
              </div>
            ) : (
              /* Form */
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      errors.name ? 'border-red-500' : 'border-gray-300'
                    }`}
                    disabled={isSubmitting}
                    placeholder="Enter your full name"
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      errors.email ? 'border-red-500' : 
                      formData.email.trim() && !validateEmail(formData.email.trim()) ? 'border-red-500' :
                      formData.email.trim() && validateEmail(formData.email.trim()) ? 'border-green-500' :
                      'border-gray-300'
                    }`}
                    disabled={isSubmitting}
                    placeholder="Enter your email address"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                  )}
                  {formData.email.trim() && !validateEmail(formData.email.trim()) && !errors.email && (
                    <p className="mt-1 text-sm text-red-600">Please enter a valid email address</p>
                  )}
                  {formData.email.trim() && validateEmail(formData.email.trim()) && (
                    <p className="mt-1 text-sm text-green-600">✓ Valid email address</p>
                  )}
                </div>

                <div>
                  <label htmlFor="organization" className="block text-sm font-medium text-gray-700 mb-1">
                    Organization <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="organization"
                    value={formData.organization}
                    onChange={(e) => handleInputChange('organization', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      errors.organization ? 'border-red-500' : 'border-gray-300'
                    }`}
                    disabled={isSubmitting}
                    placeholder="Enter your organization name"
                  />
                  {errors.organization && (
                    <p className="mt-1 text-sm text-red-600">{errors.organization}</p>
                  )}
                </div>

                {/* Submit Button */}
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting || !isFormValid}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#1F2B59] hover:bg-[#18244C] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1F2B59] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Submit Request
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default PartnershipModal
